import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import {
  getGmailConnectionStatus,
  getGmailConnectUrl,
  disconnectGmail,
  triggerGmailImport,
  fetchBankConfigs,
  createBankConfig,
  updateBankConfig,
  deleteBankConfig,
  type GmailConnectionStatus,
  type BankConfig,
} from "@/services/gmailIntegrationApi";
import {
  fetchApiKeys,
  createApiKey,
  deleteApiKey,
  type ApiKey,
} from "@/services/apiKeyApi";

interface SettingsState {
  // Gmail
  gmailStatus: GmailConnectionStatus | null;
  gmailError: string | null;
  connecting: boolean;
  disconnecting: boolean;
  importing: boolean;
  importResult: string | null;
  bankConfigs: BankConfig[];
  loadingConfigs: boolean;

  // API Keys
  keys: ApiKey[];
  loadingKeys: boolean;
  newRawKey: string | null;
  apiKeyError: string | null;
  deletingKeyId: string | null;

  // Actions — init
  loadAll: () => void;

  // Actions — Gmail
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  triggerImport: () => Promise<void>;
  addBankConfig: (
    label: string,
    senderPattern: string,
    pdfPassword: string,
    accountNumber: string,
  ) => Promise<void>;
  saveBankConfig: (
    id: string,
    label: string,
    senderPattern: string,
    pdfPassword: string,
    accountNumber: string,
  ) => Promise<void>;
  removeBankConfig: (id: string) => Promise<void>;

  // Actions — API Keys
  createKey: (name: string) => Promise<void>;
  deleteKey: (id: string) => Promise<void>;
  dismissNewKey: () => void;
}

export const useSettingsStore = create<SettingsState>((set) => ({
  // Gmail initial state
  gmailStatus: null,
  gmailError: null,
  connecting: false,
  disconnecting: false,
  importing: false,
  importResult: null,
  bankConfigs: [],
  loadingConfigs: true,

  // API Keys initial state
  keys: [],
  loadingKeys: true,
  newRawKey: null,
  apiKeyError: null,
  deletingKeyId: null,

  async loadAll() {
    const [gmailResult, configsResult, keysResult] = await Promise.allSettled([
      getGmailConnectionStatus(),
      fetchBankConfigs(),
      fetchApiKeys(),
    ]);

    set({
      gmailStatus:
        gmailResult.status === "fulfilled" ? gmailResult.value : null,
      bankConfigs:
        configsResult.status === "fulfilled" ? configsResult.value : [],
      gmailError:
        configsResult.status === "rejected"
          ? "Failed to load bank configs"
          : null,
      loadingConfigs: false,
      keys: keysResult.status === "fulfilled" ? keysResult.value : [],
      apiKeyError:
        keysResult.status === "rejected" ? "Failed to load API keys" : null,
      loadingKeys: false,
    });
  },

  async connect() {
    set({ connecting: true, gmailError: null });
    try {
      const { url } = await getGmailConnectUrl();
      window.location.href = url;
    } catch {
      set({
        gmailError: "Failed to initiate Gmail connection",
        connecting: false,
      });
    }
  },

  async disconnect() {
    set({ disconnecting: true, gmailError: null });
    try {
      await disconnectGmail();
      set({ gmailStatus: { connected: false } });
    } catch {
      set({ gmailError: "Failed to disconnect Gmail" });
    } finally {
      set({ disconnecting: false });
    }
  },

  async triggerImport() {
    set({ importing: true, importResult: null, gmailError: null });
    try {
      const result = await triggerGmailImport();
      const count = result.imported;
      set({
        importResult: `Imported ${count} transaction${count !== 1 ? "s" : ""}`,
      });
    } catch {
      set({ gmailError: "Import failed" });
    } finally {
      set({ importing: false });
    }
  },

  async addBankConfig(label, senderPattern, pdfPassword, accountNumber) {
    set({ gmailError: null });
    const created = await createBankConfig({
      label,
      senderPattern,
      pdfPassword,
      ...(accountNumber !== "" && { accountNumber }),
    });
    set((s) => ({ bankConfigs: [...s.bankConfigs, created] }));
  },

  async saveBankConfig(id, label, senderPattern, pdfPassword, accountNumber) {
    const updated = await updateBankConfig(id, {
      label,
      senderPattern,
      ...(pdfPassword !== "" && { pdfPassword }),
      ...(accountNumber !== "" && { accountNumber }),
    });
    set((s) => ({
      bankConfigs: s.bankConfigs.map((c) => (c.id === id ? updated : c)),
    }));
  },

  async removeBankConfig(id) {
    set({ gmailError: null });
    try {
      await deleteBankConfig(id);
      set((s) => ({ bankConfigs: s.bankConfigs.filter((c) => c.id !== id) }));
    } catch {
      set({ gmailError: "Failed to delete bank config" });
    }
  },

  async createKey(name) {
    set({ apiKeyError: null });
    try {
      const created = await createApiKey(name);
      set((s) => ({
        newRawKey: created.key,
        keys: [
          { id: created.id, name: created.name, createdAt: created.createdAt },
          ...s.keys,
        ],
      }));
    } catch {
      set({ apiKeyError: "Failed to create API key" });
    }
  },

  async deleteKey(id) {
    set({ deletingKeyId: id, apiKeyError: null });
    try {
      await deleteApiKey(id);
      set((s) => ({
        keys: s.keys.filter((k) => k.id !== id),
        newRawKey: s.newRawKey ? null : s.newRawKey,
      }));
    } catch {
      set({ apiKeyError: "Failed to delete API key" });
    } finally {
      set({ deletingKeyId: null });
    }
  },

  dismissNewKey() {
    set({ newRawKey: null });
  },
}));

export function useGmailState() {
  return useSettingsStore(
    useShallow((s) => ({
      gmailStatus: s.gmailStatus,
      gmailError: s.gmailError,
      connecting: s.connecting,
      disconnecting: s.disconnecting,
      importing: s.importing,
      importResult: s.importResult,
      bankConfigs: s.bankConfigs,
      loadingConfigs: s.loadingConfigs,
      connect: s.connect,
      disconnect: s.disconnect,
      triggerImport: s.triggerImport,
      addBankConfig: s.addBankConfig,
      saveBankConfig: s.saveBankConfig,
      removeBankConfig: s.removeBankConfig,
    })),
  );
}

export function useApiKeysState() {
  return useSettingsStore(
    useShallow((s) => ({
      keys: s.keys,
      loadingKeys: s.loadingKeys,
      newRawKey: s.newRawKey,
      apiKeyError: s.apiKeyError,
      deletingKeyId: s.deletingKeyId,
      createKey: s.createKey,
      deleteKey: s.deleteKey,
      dismissNewKey: s.dismissNewKey,
    })),
  );
}
